import { T } from "../../global";
import { getValType } from "../../utils";

export const getSchemaAt = (
  schemas,
  infoObjectName,
  path,
  forcePointed = false
) => {
  let currentSchema = schemas[infoObjectName];
  const parts = path.split(".").filter((part) => part.length);

  if (parts.length < 1) return currentSchema;

  for (let i = 0; i < parts.length; i++) {
    const key = parts[i];
    const isLastPart = i === parts.length - 1;

    // handle array access
    if (key.startsWith("[") && key.endsWith("]")) {
      if (currentSchema.type === "array") {
        if (isLastPart) {
          // if it's the last part and it's an ID, return the schema for the actual object
          if (key !== "[*]" && key !== "[?]") {
            if (currentSchema.item.type.startsWith("__")) {
              return schemas[currentSchema.item.type.replace(/__/g, "")];
            }

            return currentSchema.item;
          }
          // otherwise, return the schema of the item in the array directly
          return currentSchema.item;
        } else if (currentSchema.item.type.startsWith("__")) {
          currentSchema = schemas[currentSchema.item.type.replace(/__/g, "")];
        }
      }

      continue;
    }

    // handle object access
    if (currentSchema[key]) {
      currentSchema = currentSchema[key];

      if (currentSchema.type && currentSchema.type.startsWith("__")) {
        // if it's the last part and forcePointed, return the schema directly, instead of the actual object
        if (isLastPart && forcePointed) return currentSchema; // ! for ListItem

        currentSchema = schemas[currentSchema.type.replace(/__/g, "")];
      } else {
        continue;
      }
    } else {
      // key not found in schema, return undefined
      return undefined;
    }
  }

  return currentSchema;
};

export const getSchemaValType = (schema, data = undefined) => {
  if (
    !schema ||
    !("type" in schema) ||
    typeof schema.type !== "string" ||
    schema.type === "object"
  ) {
    return T.OBJ;
  }

  if (schema.type.startsWith("__")) return T.PT;

  if (schema.type === "array") {
    if (schema.item.type?.startsWith("__")) {
      return T.DB;
    }

    return T.LIST;
  }

  return T.VAL;
};

/* -------------------------------------------------------------------------- */

export const sortObjectWithSchema = (obj, schema) => {
  const sortedObj = {};

  for (const [key, value] of Object.entries(schema)) {
    if (value.function === "publicIdentifier") {
      sortedObj[key] = obj[key];
      break;
    }
  }

  for (const [key, value] of Object.entries(obj)) {
    if (!sortedObj.hasOwnProperty(key)) {
      sortedObj[key] = value;
    }
  }

  return sortedObj;
};

export const sortData = (data) => {
  const sortObject = (obj) => {
    const entries = Object.entries(obj);
    entries.sort(([, a], [, b]) => {
      const isAArray = Array.isArray(a);
      const isBArray = Array.isArray(b);

      if (isAArray && isBArray) {
        // if both are arrays, put the longer one later
        return a.length - b.length;
      }

      // keep existing behavior for non-array cases
      return isAArray - isBArray;
    });

    return Object.fromEntries(entries.map(([k, v]) => [k, sortData(v)]));
  };

  if (Array.isArray(data)) return data.map(sortData);
  if (typeof data === "object" && data !== null) return sortObject(data);

  return data;
};

export const getDataIdsAt = (
  allData,
  allSchema,
  infoObjectName,
  path,
  canBeWildcard = false
) => {
  const data = allData[infoObjectName];
  const schema = allSchema[infoObjectName];

  if (path === "") {
    const ids = Array.isArray(data) ? data.map(getId) : [getId(data)];
    return canBeWildcard ? [...ids, "*"] : ids;
  }

  const parts = path.split(".");

  const traverse = (obj, schema, pathParts) => {
    if (pathParts.length === 0) {
      const ids = Array.isArray(obj) ? obj.map(getId) : [getId(obj)];
      return canBeWildcard ? [...ids, "*"] : ids;
    }

    const [part, ...rest] = pathParts;
    const isWildcard = part === "[*]";

    const objType = getSchemaValType(schema);

    if (objType === T.LIST || objType === T.DB) {
      if (isWildcard) {
        return obj.flatMap((item) => traverse(item, schema.item, rest));
      } else {
        const targetId = part.slice(1, -1);
        const item = obj.find((i) => getId(i) === targetId);

        return item ? traverse(item, schema.item, rest) : [];
      }
    } else if (objType === T.OBJ || objType === T.PT) {
      if (
        schema.type &&
        typeof schema.type !== "object" &&
        schema.type.startsWith("__")
      ) {
        // handle pointer type
        const pointedType = schema.type.replace(/__/g, "");
        const pointedData = allData[pointedType];
        const pointedSchema = allSchema[pointedType];
        const pointedObj = pointedData.find((item) => item.id === obj);

        return traverse(pointedObj, pointedSchema, pathParts);
      }

      return traverse(obj[part], schema[part], rest);
    }

    return [];
  };

  const result = traverse(data, schema, parts);
  const uniqueIds = new Set(result);
  const sortedIds = Array.from(uniqueIds).sort((a, b) => {
    if (a === "*") return 1;
    if (b === "*") return -1;
    return 0;
  });

  return sortedIds;
};

export const getPointedPath = (path) => {
  if (!path) return path;

  const parts = path.split(".");
  const lastPart = parts[parts.length - 1];

  if (lastPart.startsWith("[") && lastPart.endsWith("]")) {
    parts[parts.length - 1] = "[*]";
    return parts.join(".");
  }

  return path;
};

export const isPointedData = (data, schema) => {
  return (
    (!("type" in schema) || typeof schema.type !== "string") &&
    typeof data === "string"
  );
};

export const getPointedData = (allData, pointerSchema, pointerId) => {
  const entityName = pointerSchema.type.replace(/__/g, "");

  if (!allData[entityName]) {
    return null;
  }

  return allData[entityName].find((item) => item.id === pointerId) || null;
};

export const getDataAt = (allData, allSchema, infoObjectName, path) => {
  const data = allData[infoObjectName];
  const schema = allSchema[infoObjectName];

  if (path === "") return data;
  const parts = path.split(".");

  const traverse = (obj, schema, pathParts, ids = []) => {
    if (pathParts.length === 0) return { __path: ids, __data: obj };

    const [part, ...rest] = pathParts;
    const isWildcard = part === "[*]";

    const objType = getSchemaValType(schema);

    if (objType === T.LIST || objType === T.DB) {
      if (isWildcard) {
        return obj.flatMap((item) => {
          const itemId = getId(item);

          if (itemId !== undefined) {
            return traverse(item, schema.item, rest, [...ids, itemId]);
          }

          return [];
        });
      } else {
        const targetId = part.slice(1, -1);
        const item = obj.find((i) => getId(i) === targetId);

        if (item) return traverse(item, schema.item, rest, [...ids, targetId]);
      }
    } else if (objType === T.OBJ || objType === T.PT) {
      if (
        schema.type &&
        typeof schema.type !== "object" &&
        schema.type.startsWith("__")
      ) {
        // handle pointer type
        const pointedType = schema.type.replace(/__/g, "");
        const pointedData = allData[pointedType];
        const pointedSchema = allSchema[pointedType];
        const pointedObj = pointedData.find((item) => item.id === obj);

        return traverse(pointedObj, pointedSchema, pathParts, [...ids]);
      }

      return traverse(obj[part], schema[part], rest, [...ids, part]);
    }

    return undefined;
  };

  const result = traverse(data, schema, parts);

  const returner = Array.isArray(result)
    ? result.filter((item) => item !== undefined)
    : [result].filter((item) => item !== undefined);

  return returner;
};

export const flattenLocatedData = (locatedData) => {
  const f = locatedData.flatMap((item) => {
    if (Array.isArray(item.__data)) {
      // if __data is an array, map each element with its __path
      return item.__data.map((dataItem) => ({
        __path: [...item.__path, getId(dataItem)],
        __data: dataItem,
      }));
    } else {
      // if __data is not an array, return the item as is
      return item;
    }
  });

  return f;
};

/* -------------------------------------------------------------------------- */

export const isFakeObj = (obj) => {
  return obj && typeof obj === "object" && "__data" in obj;
};

export const getId = (obj) => {
  if (!obj) {
    console.error("getObjId: obj is undefined");
    return "?";
  }

  // for augmented objects for the child views
  if (isFakeObj(obj)) {
    const data = obj.__data;
    const dataType = getValType(data);
    // const dataType = getSchemaValType(itemSchema);

    if (dataType === T.OBJ || dataType === T.VAL)
      return obj.__path[obj.__path.length - 1];

    return "*";
  }

  const itemType = getValType(obj);

  if (itemType === T.LIST || itemType === T.DB) return "*";

  const id =
    obj.id ??
    Object.entries(obj).find(
      ([key, _]) => key.endsWith("_id") ?? key.endsWith("Id") // should be safe to remove now
    )?.[1];

  // return id?.toString() ?? undefined;
  return id?.toString() ?? obj.toString(); // ! VERY DANGEROUS
};

/* -------------------------------------------------------------------------- */

export const appendPath = (path, key) => {
  return path ? `${path}.${key}` : key;
};

export const recoverPath = (path, pathParts) => {
  // if (!path || !pathParts || path.split(".").length !== pathParts.length) {
  if (!path || !pathParts) {
    return path;
  }

  const pathSegments = path.split(".");

  // ! THINK THROUGH THIS
  return pathParts
    .map((part, index) => {
      if (pathSegments[index] === "[*]" || !pathSegments[index])
        return `[${part}]`;

      return pathSegments[index];
    })
    .join(".");

  // const pathSegments = path.split(".");

  // return pathSegments
  //   .map((segment, index) => {
  //     if (segment === "[*]")
  //       return pathParts[index] ? `[${pathParts[index]}]` : segment;

  //     return segment;
  //   })
  //   .join(".");
};

export const getQueryPath = (path, levelUp = 1) => {
  const bracketParts = path.match(/\[.*?\]/g) || [];
  const bracketCount = bracketParts.length;
  levelUp = Math.min(Math.max(1, levelUp), bracketCount);

  const parts = path.split(/(\[.*?\])/);
  const reversedParts = parts.reverse();

  let remainingLevelUp = levelUp;
  const processedParts = reversedParts.map((part) => {
    if (part.startsWith("[") && part.endsWith("]"))
      if (remainingLevelUp > 0) {
        remainingLevelUp--;
        return "[*]";
      }

    return part;
  });

  return processedParts.reverse().join("");
};

export const getObjectIdFromPathList = (path) => {
  const firstItem = path.split(".")[0];
  return firstItem.replace(/^\[|\]$/g, "");
};

export const getPathComponents = (infoObjectName, path) => {
  const infoObjectComponent = {
    order: -1,
    type: "infoObject",
    value: infoObjectName,
  };

  if (path === "") return [infoObjectComponent];

  const parts = path.split(".");

  return [
    infoObjectComponent,
    ...parts.map((part, order) => {
      if (part.startsWith("[") && part.endsWith("]"))
        return {
          order,
          type: "id",
          value: part,
        };

      return {
        order,
        type: "key",
        value: part,
      };
    }),
  ];
};

export const updateComponent = (components, order, newComponent) => {
  return components.map((c) => (c.order === order ? newComponent : c));
};

export const pathFromComponents = (components, targetOrder = undefined) => {
  const relevantComponents = components.filter(
    (c) => c.type === "id" || c.type === "key"
  );

  if (targetOrder === undefined) {
    return relevantComponents.map((c) => c.value).join(".");
  } else {
    return relevantComponents
      .filter((c) => c.order <= targetOrder)
      .map((c) => c.value)
      .join(".");
  }
};

export const canIdBeWildcard = (order, pathComponents) => {
  const component = pathComponents.find((c) => c.order === order);

  if (component.type !== "id") return false;
  if (component.value === "[*]") return true;

  return !pathComponents
    .filter((c) => c.order > order)
    .some((c) => c.type === "id" && c.value !== "[*]");
};

/* -------------------------------------------------------------------------- */

export const isHighlighted = (highlightedInfoObject, infoObject, id) => {
  return (
    highlightedInfoObject &&
    highlightedInfoObject.infoObject === infoObject &&
    highlightedInfoObject.id === id
  );
};

/* -------------------------------------------------------------------------- */

export const prettierTitle = (title) => {
  return title
    .replace(/^(__|\w+:)?/, "") // remove leading __ or T:
    .replace(/_/g, " ") // replace underscores with spaces
    .toLowerCase() // convert to lowercase
    .replace(/\b\w/g, (c) => c.toUpperCase()) // capitalize first letter of each word
    .trim(); // remove any leading or trailing whitespace
};
